import { useState } from "react";
import SEO from "../components/SEO";
import EquivalenciesVisualizer from "../components/EquivalenciesVisualizer";
import { exportToPDF } from "../utils/pdfExport";
import {Shirt, Droplets, Download, Share2} from 'lucide-react';

export default function FashionCalculator() {
  const [monthlyClothes, setMonthlyClothes] = useState("");
  const [clothingType, setClothingType] = useState("mixed");
  const [shoppingHabit, setShoppingHabit] = useState("moderate");
  const [emissions, setEmissions] = useState<number | null>(null);
  const [waterWasted, setWaterWasted] = useState<number | null>(null);
  const [breakdown, setBreakdown] = useState<{production: number; shipping: number; disposal: number} | null>(null);

  const calculateEmissions = () => {
    const items = parseFloat(monthlyClothes) || 0;
    
    // Base emission factors (kg CO2 per item)
    const typeFactors: Record<string, number> = {
      "basics": 5.5, // t-shirts, basics
      "mixed": 12.0, // average clothing
      "luxury": 25.0, // high-end, complex items
    };

    // Shopping habit multipliers
    const habitFactors: Record<string, number> = {
      "thrift": 0.3, // secondhand has much lower impact
      "moderate": 1.0,
      "fast-fashion": 1.4, // trend-driven, lower quality
    };

    const baseEmission = items * typeFactors[clothingType] * habitFactors[shoppingHabit];
    
    // Annual emissions
    const production = (baseEmission * 12 * 0.7) / 1000; // 70% from production
    const shipping = (baseEmission * 12 * 0.2) / 1000; // 20% from shipping
    const disposal = (baseEmission * 12 * 0.1) / 1000; // 10% from disposal/waste

    const total = production + shipping + disposal;
    
    // Water calculation: ~2,700 gallons per cotton t-shirt equivalent
    const water = items * 12 * 2700 * (typeFactors[clothingType] / 5.5) * habitFactors[shoppingHabit];

    setEmissions(parseFloat(total.toFixed(2)));
    setWaterWasted(Math.round(water));
    setBreakdown({
      production: parseFloat(production.toFixed(2)),
      shipping: parseFloat(shipping.toFixed(2)),
      disposal: parseFloat(disposal.toFixed(2)),
    });
  };

  const shareScore = () => {
    const text = `My fast fashion footprint: ${emissions} tons CO₂ & ${waterWasted?.toLocaleString()} gallons of water per year. Calculate yours at www.greenhousegascalculator.com/fashion`;
    if (navigator.share) {
      navigator.share({ title: "Fashion Carbon Footprint", text });
    } else {
      navigator.clipboard.writeText(text);
      alert("Fashion footprint copied to clipboard!");
    }
  };

  const schema = {
    "@context": "https://schema.org",
    "@type": "WebApplication",
    "name": "Fast Fashion Carbon Footprint Calculator",
    "url": "https://www.greenhousegascalculator.com/fashion",
    "description": "Calculate the environmental impact of your wardrobe. Measure CO2 emissions and water waste from clothing purchases.",
    "applicationCategory": "UtilityApplication",
  };

  return (
    <div className="bg-white">
      <SEO
        title="Fast Fashion Carbon Footprint Calculator | Wardrobe Emissions Calculator"
        description="Free fast fashion carbon footprint calculator. Calculate CO2 emissions and water waste from your clothing purchases. Learn the true cost of fast fashion and sustainable alternatives."
        keywords="fast fashion carbon footprint calculator, clothing emissions calculator, wardrobe footprint, sustainable fashion calculator, fashion waste calculator"
        canonical="https://www.greenhousegascalculator.com/fashion"
        schema={schema}
      />

      {/* Hero */}
      <section className="bg-gradient-to-b from-purple-50 to-white py-16">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center">
            <div className="flex justify-center mb-4">
              <Shirt className="w-16 h-16 text-[#2E8B57]" />
            </div>
            <h1 className="text-4xl md:text-5xl font-bold text-gray-900 mb-4">
              Fast Fashion Carbon Footprint Calculator
            </h1>
            <p className="text-xl text-gray-600">
              Discover the hidden environmental cost of your wardrobe
            </p>
          </div>
        </div>
      </section>

      {/* Educational Article */}
      <section className="py-12 bg-white">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <article className="prose prose-lg max-w-none">
            <h2 className="text-3xl font-bold text-gray-900 mb-6">
              The True Cost of Fast Fashion
            </h2>

            <div className="bg-red-50 border-l-4 border-red-400 p-6 mb-8">
              <p className="text-lg font-semibold text-gray-900">
                ⚠️ The fashion industry produces <span className="text-red-600">10% of global carbon emissions</span> - more than international flights and maritime shipping combined!
              </p>
            </div>

            <p className="text-gray-700 mb-6">
              Fast fashion has transformed how we shop, offering trendy clothes at rock-bottom prices. But this convenience comes at a devastating environmental cost that extends far beyond what we see in stores.
            </p>

            <h3 className="text-2xl font-semibold text-gray-900 mt-8 mb-4">
              The Hidden Environmental Impact
            </h3>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-8">
              <div className="bg-blue-50 p-6 rounded-lg">
                <div className="flex items-center gap-2 mb-3">
                  <Droplets className="w-6 h-6 text-blue-600" />
                  <h4 className="font-semibold text-gray-900">Water Consumption</h4>
                </div>
                <p className="text-gray-700">One cotton t-shirt requires 2,700 gallons of water - enough for one person to drink for 2.5 years!</p>
              </div>

              <div className="bg-orange-50 p-6 rounded-lg">
                <h4 className="font-semibold text-gray-900 mb-3">Microplastic Pollution</h4>
                <p className="text-gray-700">Synthetic fabrics shed 700,000 microplastic fibers per wash, polluting oceans and entering the food chain.</p>
              </div>

              <div className="bg-green-50 p-6 rounded-lg">
                <h4 className="font-semibold text-gray-900 mb-3">Textile Waste</h4>
                <p className="text-gray-700">85% of textiles end up in landfills - equivalent to one garbage truck of clothes every second globally.</p>
              </div>

              <div className="bg-purple-50 p-6 rounded-lg">
                <h4 className="font-semibold text-gray-900 mb-3">Carbon Emissions</h4>
                <p className="text-gray-700">A single pair of jeans produces 33.4 kg of CO₂ - equivalent to driving 69 miles in an average car.</p>
              </div>
            </div>

            <h3 className="text-2xl font-semibold text-gray-900 mt-8 mb-4">
              7 Sustainable Fashion Alternatives
            </h3>

            <div className="space-y-4 mb-6">
              <div className="bg-green-50 p-4 rounded-lg border-l-4 border-green-500">
                <h4 className="font-semibold text-gray-900 mb-2">1. Buy Secondhand & Thrift</h4>
                <p className="text-gray-700">Thrifting extends clothing life and reduces production demand by 70%. One person's trash is another's treasure!</p>
              </div>

              <div className="bg-green-50 p-4 rounded-lg border-l-4 border-green-500">
                <h4 className="font-semibold text-gray-900 mb-2">2. Choose Quality Over Quantity</h4>
                <p className="text-gray-700">Invest in durable, timeless pieces. A $100 item worn 100 times is more sustainable than 10 $10 items worn once.</p>
              </div>

              <div className="bg-green-50 p-4 rounded-lg border-l-4 border-green-500">
                <h4 className="font-semibold text-gray-900 mb-2">3. Rent for Special Occasions</h4>
                <p className="text-gray-700">Wedding guest? Rent designer outfits instead of buying clothes you'll wear once. Saves money and emissions!</p>
              </div>

              <div className="bg-green-50 p-4 rounded-lg border-l-4 border-green-500">
                <h4 className="font-semibold text-gray-900 mb-2">4. Repair & Upcycle</h4>
                <p className="text-gray-700">Learn basic sewing skills. Turn old jeans into shorts, add patches, or customize with embroidery.</p>
              </div>

              <div className="bg-green-50 p-4 rounded-lg border-l-4 border-green-500">
                <h4 className="font-semibold text-gray-900 mb-2">5. Support Sustainable Brands</h4>
                <p className="text-gray-700">Look for certifications: GOTS (organic), Fair Trade, or B Corp. These brands prioritize environmental responsibility.</p>
              </div>

              <div className="bg-green-50 p-4 rounded-lg border-l-4 border-green-500">
                <h4 className="font-semibold text-gray-900 mb-2">6. Organize Clothing Swaps</h4>
                <p className="text-gray-700">Host swaps with friends or join community events. Refresh your wardrobe without buying anything new!</p>
              </div>

              <div className="bg-green-50 p-4 rounded-lg border-l-4 border-green-500">
                <h4 className="font-semibold text-gray-900 mb-2">7. Practice the 30-Wear Rule</h4>
                <p className="text-gray-700">Before buying, ask: "Will I wear this 30 times?" If not, skip it. This simple rule prevents impulse purchases.</p>
              </div>
            </div>

            <div className="bg-blue-50 p-6 rounded-lg mt-8">
              <p className="text-gray-700 font-medium">
                💡 <strong>The Bottom Line:</strong> The most sustainable garment is the one already in your closet. Wear what you own, buy less, choose well, and make it last.
              </p>
            </div>
          </article>
        </div>
      </section>

      {/* Calculator */}
      <section className="py-16 bg-gray-50">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="bg-white rounded-2xl p-8 shadow-lg border border-gray-200">
            <h2 className="text-2xl font-bold text-gray-900 mb-6">
              Calculate Your Wardrobe's Environmental Impact
            </h2>

            <div className="space-y-6 mb-8">
              <div className="bg-purple-50 p-6 rounded-xl">
                <label className="font-semibold text-gray-900 block mb-4">
                  How Many New Clothes Do You Buy Per Month?
                </label>
                <input
                  type="number"
                  value={monthlyClothes}
                  onChange={(e) => setMonthlyClothes(e.target.value)}
                  placeholder="e.g., 3"
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#2E8B57]"
                />
                <p className="text-xs text-gray-500 mt-2">
                  Include all clothing: shirts, pants, shoes, accessories, etc.
                </p>
              </div>

              <div className="bg-purple-50 p-6 rounded-xl">
                <label className="font-semibold text-gray-900 block mb-4">
                  Typical Clothing Type
                </label>
                <select
                  value={clothingType}
                  onChange={(e) => setClothingType(e.target.value)}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#2E8B57]"
                >
                  <option value="basics">Basic Items (t-shirts, simple jeans)</option>
                  <option value="mixed">Mixed (Average clothing)</option>
                  <option value="luxury">Designer/Complex Items (dresses, suits)</option>
                </select>
              </div>

              <div className="bg-purple-50 p-6 rounded-xl">
                <label className="font-semibold text-gray-900 block mb-4">
                  Shopping Habits
                </label>
                <select
                  value={shoppingHabit}
                  onChange={(e) => setShoppingHabit(e.target.value)}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#2E8B57]"
                >
                  <option value="thrift">Mostly Secondhand/Thrift (70% lower impact!)</option>
                  <option value="moderate">Mix of New & Secondhand</option>
                  <option value="fast-fashion">Fast Fashion (Trend-driven, frequent)</option>
                </select>
              </div>
            </div>

            <button
              onClick={calculateEmissions}
              className="w-full bg-[#2E8B57] text-white py-4 rounded-lg font-semibold hover:bg-[#267347] transition-colors"
            >
              Calculate Fashion Footprint
            </button>

            {emissions !== null && waterWasted !== null && breakdown && (
              <div className="mt-8 space-y-4">
                <div className="bg-gradient-to-r from-purple-50 to-blue-50 p-6 rounded-xl border-2 border-[#2E8B57]">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <div className="text-center">
                      <p className="text-gray-600 mb-2">Annual Carbon Emissions</p>
                      <p className="text-3xl font-bold text-[#2E8B57]">
                        {emissions} tons CO₂e
                      </p>
                    </div>
                    <div className="text-center">
                      <div className="flex items-center justify-center gap-2 mb-2">
                        <Droplets className="w-5 h-5 text-blue-500" />
                        <p className="text-gray-600">Water Wasted</p>
                      </div>
                      <p className="text-3xl font-bold text-blue-600">
                        {waterWasted.toLocaleString()} gal
                      </p>
                    </div>
                  </div>
                  <p className="text-sm text-gray-500 mt-4 text-center">
                    {emissions < 0.5 ? "🌟 Excellent! Sustainable wardrobe!" : 
                     emissions < 1.5 ? "✅ Good! Below average impact" :
                     "⚠️ High impact - consider sustainable alternatives"}
                  </p>
                </div>

                <div className="bg-gray-50 p-6 rounded-xl">
                  <h3 className="font-semibold text-gray-900 mb-4">Emissions Breakdown</h3>
                  <div className="space-y-3">
                    <div className="flex justify-between items-center">
                      <span className="text-gray-600">Production & Manufacturing</span>
                      <span className="font-semibold">{breakdown.production} tons ({Math.round((breakdown.production/emissions)*100)}%)</span>
                    </div>
                    <div className="flex justify-between items-center">
                      <span className="text-gray-600">Shipping & Transportation</span>
                      <span className="font-semibold">{breakdown.shipping} tons ({Math.round((breakdown.shipping/emissions)*100)}%)</span>
                    </div>
                    <div className="flex justify-between items-center">
                      <span className="text-gray-600">Disposal & Waste</span>
                      <span className="font-semibold">{breakdown.disposal} tons ({Math.round((breakdown.disposal/emissions)*100)}%)</span>
                    </div>
                  </div>
                </div>

                <div className="bg-blue-50 p-6 rounded-xl">
                  <h3 className="font-semibold text-gray-900 mb-3">💧 Water Impact Equivalents:</h3>
                  <ul className="space-y-2 text-gray-700">
                    <li>• {Math.round(waterWasted / 8)} days of drinking water for one person</li>
                    <li>• {Math.round(waterWasted / 80)} showers (8 min each)</li>
                    <li>• {Math.round(waterWasted / 2700)} cotton t-shirts worth of water</li>
                  </ul>
                </div>

                <EquivalenciesVisualizer emissions={emissions} />

                <div className="flex gap-4">
                  <button
                    onClick={() => exportToPDF({
                      title: "Fashion Footprint Report",
                      totalEmissions: emissions,
                      breakdown,
                      calculationType: "fashion"
                    })}
                    className="flex-1 bg-[#2E8B57] text-white px-6 py-3 rounded-lg font-semibold hover:bg-[#267347] transition-colors flex items-center justify-center gap-2"
                  >
                    <Download className="w-5 h-5" />
                    Export Report
                  </button>
                  <button
                    onClick={shareScore}
                    className="flex-1 bg-purple-500 text-white px-6 py-3 rounded-lg font-semibold hover:bg-purple-600 transition-colors flex items-center justify-center gap-2"
                  >
                    <Share2 className="w-5 h-5" />
                    Share Your Score
                  </button>
                </div>
              </div>
            )}
          </div>
        </div>
      </section>
    </div>
  );
}
